import { RoomRef, VideoStreamMode, VideoTicket } from '@features/video/types';

// Запас до истечения билета: не выдаем ссылку, которая протухнет по дороге
const EXPIRY_MARGIN_MS = 60 * 1000;

// Payload JWT читаем без проверки подписи: ее проверяет сервер связи
export function decodeTicket(ticket: string): VideoTicket | null {
  const part = ticket.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder().decode(bytes));
    // audience по RFC 7519 бывает массивом — берем первый адрес
    const aud = Array.isArray(payload.aud) ? payload.aud[0] : payload.aud;
    if (typeof aud !== 'string' || !aud) return null;
    return { ...payload, aud } as VideoTicket;
  } catch {
    return null;
  }
}

export function isTicketExpired(ticket: VideoTicket): boolean {
  return ticket.exp * 1000 - EXPIRY_MARGIN_MS <= Date.now();
}

// Ссылка телефона: клуб и турнир в пути, чтобы телефон запросил их без входа и не
// разбирал формат claim `room`
export function getStreamLink(
  clubId: string,
  tournamentId: string,
  mode: VideoStreamMode,
  ticket: string,
): string {
  return `${window.location.origin}/stream/${clubId}/${tournamentId}/${mode}/${ticket}`;
}

// Страница судьи: первая комната — путь, остальные комнаты мультикомнаты — search `rooms`;
// с userId — сразу с открытой приватной комнатой игрока (ссылку можно переслать другому судье)
export function getRefereeRoomPath(
  clubId: string,
  rooms: RoomRef | RoomRef[],
  privateUserId?: string,
): string {
  const [first, ...rest] = Array.isArray(rooms) ? rooms : [rooms];
  if (!first) return `/club/${clubId}`;
  const base = `/club/${clubId}/tournament/${first.tournamentId}/referee/${first.roomNumber}`;
  const path = privateUserId ? `${base}/private/${privateUserId}` : base;
  const search = formatRoomsSearch(rest);
  return search ? `${path}?rooms=${encodeURIComponent(search)}` : path;
}

export const roomKey = (room: RoomRef): string => `${room.tournamentId}:${room.roomNumber}`;

// Комнаты в search: `<tournamentId>:<номер>` через запятую; битые пары пропускаем
export function parseRoomsSearch(raw: string | undefined): RoomRef[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((pair) => {
      const at = pair.lastIndexOf(':');
      const roomNumber = Number(pair.slice(at + 1));
      const tournamentId = pair.slice(0, at);
      return at > 0 && Number.isInteger(roomNumber) && roomNumber >= 1
        ? { tournamentId, roomNumber }
        : null;
    })
    .filter((room) => room !== null);
}

export function formatRoomsSearch(rooms: RoomRef[]): string | undefined {
  return rooms.length ? rooms.map(roomKey).join(',') : undefined;
}
